import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Loader2, RefreshCw, Tags } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataTable } from "@/components/DataTable";
import { StatusPill } from "@/components/StatusPill";
import { InlineDetailPanel } from "@/components/InlineDetailPanel";
import {
  adminApi,
  type CatalogProduct,
  type ListCatalogOutput,
  type SetCatalogPriceOutput,
} from "@/lib/api";
import { LIST_STALE_MS, queryKeys } from "@/lib/queries";
import { queryTableProps } from "@/lib/data-table";
import { formatLondon, isoUtc } from "@/lib/format-time";
import { gbp, pencePerTag, priceConfirmText } from "@/lib/catalog";

export const Route = createFileRoute("/_authed/catalog")({
  component: CatalogPage,
});

const errMsg = (e: unknown) => (e instanceof Error ? e.message : "Request failed");

function CatalogPage() {
  const queryClient = useQueryClient();
  const [selectedSku, setSelectedSku] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  // Lives here, not in PriceEditor: a successful change swaps the row's stripePriceId,
  // which re-keys (remounts) the editor and would drop editor-local state.
  const [priceNotice, setPriceNotice] = useState<string | null>(null);

  const query = useQuery({
    queryKey: queryKeys.catalog.all,
    queryFn: async () => (await adminApi.listCatalog()()).data,
    staleTime: LIST_STALE_MS,
  });
  const products = query.data?.products ?? [];
  const mode = query.data?.stripeMode ?? "test";
  const selected = products.find((p) => p.sku === selectedSku) ?? null;

  const sync = useMutation({
    mutationFn: async () => (await adminApi.syncCatalog()()).data,
    onSuccess: async (r) => {
      setBanner(
        `Sync complete: ${r.upserted.length} upserted, ${r.deactivated.length} deactivated, ${r.skipped} skipped.`,
      );
      await queryClient.invalidateQueries({ queryKey: queryKeys.catalog.all });
    },
    onError: (e) => setBanner(`Sync failed: ${errMsg(e)}`),
  });

  function runSync() {
    if (
      !window.confirm(
        `Sync the catalogue from ${mode === "live" ? "LIVE" : "SANDBOX"} Stripe? Rows are upserted from Stripe; rows no longer active in Stripe are deactivated.`,
      )
    )
      return;
    setBanner(null);
    sync.mutate();
  }

  /** Replace one row in the cached list with the server's fresh copy. */
  function replaceRow(row: CatalogProduct) {
    queryClient.setQueryData<ListCatalogOutput>(queryKeys.catalog.all, (old) =>
      old ? { ...old, products: old.products.map((p) => (p.sku === row.sku ? row : p)) } : old,
    );
  }

  const columns: ColumnDef<CatalogProduct>[] = [
    { header: "SKU", cell: ({ row }) => <span className="font-mono text-xs">{row.original.sku}</span> },
    { header: "Type", accessorKey: "type" },
    { header: "Pack label", accessorKey: "packLabel" },
    { header: "Units", accessorKey: "units" },
    {
      header: "Price",
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{gbp(row.original.pricePence)}</div>
          <div className="text-[11px] text-muted-foreground">
            {pencePerTag(row.original.pricePence, row.original.units)} / tag
          </div>
        </div>
      ),
    },
    {
      header: "Active",
      cell: ({ row }) =>
        row.original.isActive ? (
          <StatusPill tone="success">Active</StatusPill>
        ) : (
          <StatusPill tone="neutral">Inactive</StatusPill>
        ),
    },
    { header: "Popular", cell: ({ row }) => (row.original.isPopular ? "Yes" : "—") },
    { header: "Sort", accessorKey: "sortOrder" },
    {
      header: "Stripe price id",
      cell: ({ row }) => {
        const id = row.original.stripePriceId;
        return id ? (
          <span className="block max-w-[140px] truncate font-mono text-[11px]" title={id}>
            {id}
          </span>
        ) : (
          "—"
        );
      },
    },
    {
      header: "Updated",
      cell: ({ row }) =>
        row.original.updatedAt ? (
          <span title={isoUtc(row.original.updatedAt)}>{formatLondon(row.original.updatedAt)}</span>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Tags className="h-5 w-5" /> Catalogue
            {query.data &&
              (mode === "live" ? (
                <StatusPill tone="warning">Live Stripe</StatusPill>
              ) : (
                <StatusPill tone="info">Sandbox</StatusPill>
              ))}
          </h1>
          <p className="text-sm text-muted-foreground">
            Tag packs sold on the website and in the app. Prices change by creating a new Stripe
            Price. Audit-logged.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => void query.refetch()} disabled={query.isFetching}>
            <RefreshCw className={`h-4 w-4 ${query.isFetching ? "animate-spin" : ""}`} />
          </Button>
          <Button size="sm" variant="outline" onClick={runSync} disabled={sync.isPending}>
            {sync.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Sync from Stripe
          </Button>
        </div>
      </div>

      {banner && <div className="rounded-md border bg-muted px-3 py-2 text-sm">{banner}</div>}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Products{" "}
            <span className="text-sm font-normal text-muted-foreground">{products.length} total</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <DataTable
            data={products}
            columns={columns}
            onRowClick={(r) => {
              setPriceNotice(null);
              setSelectedSku(r.sku === selectedSku ? null : r.sku);
            }}
            emptyMessage="No catalogue rows. Run Sync from Stripe."
            rowKey={(r) => r.sku}
            footerNote={
              query.data
                ? `${products.length} rows · read ${formatLondon(Date.parse(query.data.asOf))} — click a row to edit`
                : undefined
            }
            {...queryTableProps(query)}
          />

          {selected && (
            <InlineDetailPanel
              title={`${selected.sku} — ${selected.name}`}
              onClose={() => {
                setPriceNotice(null);
                setSelectedSku(null);
              }}
            >
              <div className="space-y-5">
                {!selected.isActive && (
                  <p className="flex items-center gap-2 text-sm text-muted-foreground">
                    <StatusPill tone="neutral">Inactive</StatusPill> not sold — activate in Stripe,
                    then Sync
                  </p>
                )}
                <PriceEditor
                  key={`${selected.sku}:${selected.stripePriceId}`}
                  row={selected}
                  mode={mode}
                  notice={priceNotice}
                  onSaved={(product, warning) => {
                    setPriceNotice(
                      warning
                        ? "The price was changed, but the old Stripe Price was not archived. Archive it in the Stripe Dashboard; the detail is in the audit log."
                        : null,
                    );
                    replaceRow(product);
                  }}
                />
                <DisplayEditor
                  key={`d:${selected.sku}:${selected.isPopular}:${selected.sortOrder}`}
                  row={selected}
                  onSaved={replaceRow}
                />
              </div>
            </InlineDetailPanel>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PriceEditor({
  row,
  mode,
  notice,
  onSaved,
}: {
  row: CatalogProduct;
  mode: "live" | "test";
  notice: string | null;
  onSaved: (r: CatalogProduct, warning?: SetCatalogPriceOutput["warning"]) => void;
}) {
  const [draft, setDraft] = useState((row.pricePence / 100).toFixed(2));
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const gbpValue = Number(draft);
  const valid = draft.trim() !== "" && Number.isFinite(gbpValue) && gbpValue >= 0.01 && gbpValue <= 1000;
  const newPence = valid ? Math.round(gbpValue * 100) : null;

  const save = useMutation({
    mutationFn: async (pricePence: number) =>
      (
        await adminApi.setCatalogPrice()({
          sku: row.sku,
          pricePence,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        })
      ).data,
    onSuccess: (res: SetCatalogPriceOutput) => onSaved(res.product, res.warning),
    onError: (e) => setError(errMsg(e)),
  });

  function submit() {
    if (newPence === null) {
      setError("Enter a price between £0.01 and £1000.00");
      return;
    }
    if (newPence === row.pricePence) {
      setError("Price is unchanged");
      return;
    }
    if (!window.confirm(priceConfirmText(row.sku, row.pricePence, newPence, mode))) return;
    setError(null);
    save.mutate(newPence);
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium">Change price</h3>
      <div className="flex flex-wrap items-end gap-3">
        <label className="space-y-1 text-sm">
          <span className="text-muted-foreground">Price (GBP)</span>
          <Input
            type="number"
            step="0.01"
            min="0.01"
            max="1000"
            className="w-32"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={save.isPending}
          />
        </label>
        <label className="min-w-[220px] flex-1 space-y-1 text-sm">
          <span className="text-muted-foreground">Reason (optional)</span>
          <Input
            value={reason}
            maxLength={500}
            onChange={(e) => setReason(e.target.value)}
            disabled={save.isPending}
          />
        </label>
        <Button size="sm" onClick={submit} disabled={save.isPending || !valid}>
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save price"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        {newPence !== null
          ? `${gbp(newPence)} total, ${pencePerTag(newPence, row.units)} per tag (${row.units} units)`
          : "Enter a valid price"}
      </p>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {notice && (
        <div className="rounded-md border border-warning/40 bg-warning/15 px-3 py-2 text-sm text-warning">
          {notice}
        </div>
      )}
    </div>
  );
}

function DisplayEditor({
  row,
  onSaved,
}: {
  row: CatalogProduct;
  onSaved: (r: CatalogProduct) => void;
}) {
  const [popular, setPopular] = useState(row.isPopular);
  const [sort, setSort] = useState(String(row.sortOrder));
  const [error, setError] = useState<string | null>(null);

  const sortNum = Number(sort);
  const sortValid = sort.trim() !== "" && Number.isInteger(sortNum) && sortNum >= 0 && sortNum <= 9999;
  const dirty = popular !== row.isPopular || (sortValid && sortNum !== row.sortOrder);

  const save = useMutation({
    mutationFn: async () =>
      (
        await adminApi.updateCatalogDisplay()({
          sku: row.sku,
          ...(popular !== row.isPopular ? { isPopular: popular } : {}),
          ...(sortValid && sortNum !== row.sortOrder ? { sortOrder: sortNum } : {}),
        })
      ).data,
    onSuccess: (product) => {
      setError(null);
      onSaved(product);
    },
    onError: (e) => setError(errMsg(e)),
  });

  return (
    <div className="space-y-2 border-t pt-4">
      <h3 className="text-sm font-medium">Display</h3>
      <div className="flex flex-wrap items-end gap-4">
        <label className="flex items-center gap-2 pb-2 text-sm">
          <input
            type="checkbox"
            checked={popular}
            onChange={(e) => setPopular(e.target.checked)}
            disabled={save.isPending}
          />
          Popular
        </label>
        <label className="space-y-1 text-sm">
          <span className="text-muted-foreground">Sort (0–9999)</span>
          <Input
            type="number"
            step="1"
            min="0"
            max="9999"
            className="w-28"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            disabled={save.isPending}
          />
        </label>
        <Button
          size="sm"
          onClick={() => {
            setError(null);
            save.mutate();
          }}
          disabled={save.isPending || !dirty || !sortValid}
        >
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save display"}
        </Button>
      </div>
      {!sortValid && <p className="text-sm text-destructive">Sort must be a whole number from 0 to 9999</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
